/**
 * @file src/_modulos/prenota/tabela/layouts/Header.tsx
 * @description Header simplificado de coluna da tabela
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas renderiza header de coluna
 * - YAGNI: Remove animações complexas não utilizadas
 */

"use client";

import { Column } from "@tanstack/react-table";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "visuais";
import {
  isColumnSorted,
  useTableStore,
  getSortIconComponent,
  sortIcons,
} from "@PN/tabela";

/**
 * Props do componente Header
 */
type Props<TData, TValue> = {
  column: Column<TData, TValue>;
  title: string;
};

/**
 * Header de coluna da tabela
 */
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
}: Props<TData, TValue>) {
  const { sorting, setSorting, setPageIndex } = useTableStore();

  const columnId = column.id;

  const handleSortingUpdate = (desc: boolean) => {
    const newSorting = [{ id: columnId, desc }];
    setSorting(newSorting);
    setPageIndex(0);
  };

  const toggleVisibility = () => {
    column.toggleVisibility();
  };

  // Verifica se a coluna atual está sendo ordenada
  const isCurrentlySorted = isColumnSorted(sorting, columnId);

  // Obtém o ícone apropriado
  const SortIcon = getSortIconComponent(sorting, columnId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`flex items-center justify-center w-full text-[9pt] fhd:text-[10pt] qhd:text-[11pt] uhd:text-[12pt] ${
            isCurrentlySorted ? "bg-muted" : ""
          }`}
        >
          <span>{title}</span>
          <SortIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleSortingUpdate(false)}>
          <sortIcons.ascending className="mr-2 h-4 w-4" />
          Ordenar ascendente
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSortingUpdate(true)}>
          <sortIcons.descending className="mr-2 h-4 w-4" />
          Ordenar descendente
        </DropdownMenuItem>
        <DropdownMenuItem onClick={toggleVisibility}>
          <sortIcons.unsorted className="mr-2 h-4 w-4" />
          {column.getIsVisible() ? "Ocultar coluna" : "Exibir coluna"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
